import { useCallback, useEffect, useState } from 'react';

/**
 * Lightweight Phantom Wallet hook. Uses window.solana injected provider — no SDK needed.
 * Exposes: connected, publicKey, connect(), disconnect(), signMessage(), available
 */
export default function usePhantom() {
  const [publicKey, setPublicKey] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const provider = typeof window !== 'undefined' ? window?.solana : null;
  const available = !!(provider && provider.isPhantom);

  useEffect(() => {
    if (!available) return;
    // Try to silently reconnect if user already approved
    const tryReconnect = async () => {
      try {
        const res = await provider.connect({ onlyIfTrusted: true });
        if (res?.publicKey) setPublicKey(res.publicKey.toString());
      } catch (e) { /* silent */ }
    };
    tryReconnect();
    const onConnect = (pk) => setPublicKey(pk.toString());
    const onDisconnect = () => setPublicKey(null);
    provider.on('connect', onConnect);
    provider.on('disconnect', onDisconnect);
    return () => {
      try { provider.removeListener('connect', onConnect); provider.removeListener('disconnect', onDisconnect); } catch {}
    };
  }, [available, provider]);

  const connect = useCallback(async () => {
    if (!available) {
      window.open('https://phantom.app/', '_blank');
      throw new Error('Phantom non installé. Une page d\'installation a été ouverte.');
    }
    setConnecting(true);
    try {
      const res = await provider.connect();
      const pk = res.publicKey.toString();
      setPublicKey(pk);
      return pk;
    } finally {
      setConnecting(false);
    }
  }, [available, provider]);

  const disconnect = useCallback(async () => {
    if (provider && available) {
      try { await provider.disconnect(); } catch {}
    }
    setPublicKey(null);
  }, [available, provider]);

  return { available, connected: !!publicKey, publicKey, connecting, connect, disconnect, provider };
}
